import { useMemo } from "react";
import {
  type OutboundWebhookEventType,
  type WebhookPayloadShape,
  useApiKeyAdminEventTypeCatalogQuery,
} from "../../../generated/graphql";
import {
  type EventTypeOptions,
  getEventTypeOptions,
} from "../event-type-options";

export type EventTypeOptionsState =
  | { status: "loading" }
  | { status: "error"; message: string; retry: () => void }
  | ({ status: "ready" } & EventTypeOptions);

/**
 * Fetches the backend event-type catalog and narrows it to the key's current
 * shapes and terminal-handoff opt-in. The create and edit dialogs both call
 * this: they pass the result to `AdvancedApiKeyFields` and block save while
 * `outOfScope` is non-empty.
 */
const useEventTypeOptions = ({
  payloadShapes,
  emitTerminalHandoffEvents,
  eventTypes,
}: {
  payloadShapes: readonly WebhookPayloadShape[];
  emitTerminalHandoffEvents: boolean;
  eventTypes: readonly OutboundWebhookEventType[];
}): EventTypeOptionsState => {
  const { data, error, refetch } = useApiKeyAdminEventTypeCatalogQuery();
  const catalog = data?.apiKeyAdminEventTypeCatalog;

  return useMemo((): EventTypeOptionsState => {
    if (catalog !== undefined) {
      return {
        status: "ready",
        ...getEventTypeOptions({
          catalog,
          payloadShapes,
          emitTerminalHandoffEvents,
          eventTypes,
        }),
      };
    }
    if (error !== undefined) {
      return {
        status: "error",
        message: error.message,
        retry: () => {
          // A failed refetch comes back through `error`, which this state
          // already renders.
          void refetch();
        },
      };
    }
    return { status: "loading" };
  }, [
    catalog,
    error,
    refetch,
    payloadShapes,
    emitTerminalHandoffEvents,
    eventTypes,
  ]);
};

export default useEventTypeOptions;
